import { useState } from "react";
import { Check, Copy } from "lucide-react";

const TONES = {
  ok: "bg-[#0F8B6D]/10 text-[#0F8B6D] border-[#0F8B6D]/30",
  deny: "bg-[#C2412D]/10 text-[#C2412D] border-[#C2412D]/30",
  warn: "bg-[#E2A33B]/15 text-[#8A5B0F] border-[#E2A33B]/40",
  info: "bg-[#2F6FB3]/10 text-[#2F6FB3] border-[#2F6FB3]/30",
  idle: "bg-slate-500/10 text-slate-600 border-slate-400/30",
};

const STATUS_TONE = {
  allowed: "ok", success: "ok", approved: "ok", operational: "ok", verified: "ok", configured: "ok", passed: "ok",
  denied: "deny", rejected: "deny", error: "deny", failed: "deny", blocked: "deny",
  pending: "warn", not_configured: "warn", in_review: "warn", in_progress: "warn", awaiting_qa: "warn",
  configured_unverified: "warn", not_connected: "warn", registered_not_authorized: "warn", authorized_no_tool_call: "warn",
  proposed: "info", inactive: "idle", out_of_scope: "idle",
};

export const StatusPill = ({ status, label, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${TONES[STATUS_TONE[status] || "idle"]}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {label || String(status).replace(/_/g, " ")}
  </span>
);

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="cc-rise mb-8 flex flex-col gap-4 border-b border-[var(--cc-line)] pb-6 md:flex-row md:items-end md:justify-between">
    <div>
      <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--cc-muted)]">{eyebrow}</p>
      <h1 data-testid="page-title" className="text-3xl font-extrabold text-[var(--cc-navy)] sm:text-4xl">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Panel = ({ title, aside, children, className = "", testId }) => (
  <section data-testid={testId} className={`cc-rise min-w-0 rounded-sm border border-[var(--cc-line)] bg-white ${className}`}>
    {title && (
      <header className="flex items-center justify-between gap-3 border-b border-[var(--cc-line)] px-5 py-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[var(--cc-navy)]">{title}</h2>
        {aside}
      </header>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Stat = ({ label, value, hint, testId }) => (
  <div className="cc-rise rounded-sm border border-[var(--cc-line)] bg-white p-5">
    <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--cc-muted)]">{label}</p>
    <p data-testid={testId} className="mt-2 font-display text-4xl font-extrabold text-[var(--cc-navy)]">{value}</p>
    {hint && <p className="mt-1 text-xs text-[var(--cc-muted)]">{hint}</p>}
  </div>
);

export const JsonView = ({ value, testId, max = "max-h-80" }) => (
  <pre data-testid={testId} className={`${max} overflow-auto whitespace-pre-wrap break-all rounded-sm bg-[var(--cc-navy)] p-4 text-[11.5px] leading-relaxed text-[#CFE0F5]`}>
    {JSON.stringify(value, null, 2)}
  </pre>
);

export const CopyField = ({ value, testId }) => {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); };
  return (
    <div className="flex items-stretch overflow-hidden rounded-sm border border-[var(--cc-line)] bg-[var(--cc-paper)]">
      <code data-testid={testId} className="min-w-0 flex-1 truncate px-3 py-2 text-xs">{value}</code>
      <button data-testid={`${testId}-copy`} onClick={copy} className="border-l border-[var(--cc-line)] px-3 text-[var(--cc-navy)] transition-colors hover:bg-[var(--cc-signal)]">
        {done ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
};

export const Loading = () => <p className="cc-pulse text-sm text-[var(--cc-muted)]" data-testid="loading">Loading telemetry…</p>;

export const ErrorNote = ({ error }) => (
  <p data-testid="error-message" className="rounded-sm border border-[#C2412D]/30 bg-[#C2412D]/10 p-3 text-sm text-[#C2412D]">{error?.message || String(error)}</p>
);

export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
